/**
 * Library → Resources. A curated index of external study sites we trust:
 * canonical text repositories, audio archives, learning aids. Hard-coded
 * for now — when the list becomes meaningful we'll promote it to a small
 * admin-managed table.
 *
 * Each entry is a card with a glyph illustration (no third-party favicons
 * fetched — privacy-friendly, fast, works offline). Cards are hand-picked
 * and grouped: Texts, Audio & Recitation, Learning, Reference.
 */

interface ResourceLink {
  url: string;
  title: string;
  description: string;
  glyph: string;
  /** Optional — surfaces below the title in a small tag. */
  hint?: string;
}

interface ResourceGroup {
  eyebrow: string;
  title: string;
  blurb: string;
  links: ResourceLink[];
}

const GROUPS: ResourceGroup[] = [
  {
    eyebrow: "Texts",
    title: "Where the corpus lives",
    blurb: "Canonical repositories of the Sanskrit literature — open, citable, persistent.",
    links: [
      {
        url: "https://sanskritdocuments.org/",
        title: "Sanskrit Documents",
        description:
          "A vast, long-running archive — Vedas, Upaniṣads, Purāṇas, stotras, sūtras. Texts in Devanāgarī and IAST, often with PDFs.",
        glyph: "ग्र",
        hint: "Repository",
      },
      {
        url: "https://gretil.sub.uni-goettingen.de/gretil.html",
        title: "GRETIL",
        description:
          "The Göttingen Register of Electronic Texts in Indian Languages. Academic depth — critical editions, machine-readable.",
        glyph: "Ω",
        hint: "Academic",
      },
      {
        url: "https://www.holy-bhagavad-gita.org/",
        title: "Bhagavad Gītā · Holy",
        description:
          "Multiple translations of the Bhagavad Gītā with verse-by-verse commentary. Useful for quick comparison.",
        glyph: "श्री",
        hint: "Verses + translations",
      },
    ],
  },
  {
    eyebrow: "Audio & Recitation",
    title: "How it should sound",
    blurb: "Recitations that show the metre, the pauses, and the lineage-specific accent.",
    links: [
      {
        url: "https://www.youtube.com/@vedaunion",
        title: "Veda Union · YouTube",
        description:
          "Our own channel — class recordings, satsang, and chanting. Subscribe for new episodes.",
        glyph: "▶",
        hint: "Our channel",
      },
      {
        url: "https://www.youtube.com/@challakerebrothersofficial4886",
        title: "Challakere Brothers",
        description:
          "Among the most respected reciters of the Vedic corpus today — careful svara, traditional pronunciation, and faithful adherence to the lineage. A reference recording when learning a new chant.",
        glyph: "स्व",
        hint: "Vedic recitation",
      },
      {
        url: "https://www.youtube.com/channel/UCDaO9KBGWibxCcLbszc76fw",
        title: "YouTube Theme channel",
        description:
          "A trusted YouTube source for clean Sanskrit and Vedic recitations. Useful when you want to hear how a sūkta or sahasranāma should sound.",
        glyph: "♪",
        hint: "Recitations",
      },
    ],
  },
  {
    eyebrow: "Learning",
    title: "If you want to study",
    blurb: "Tools and curricula for the language itself — not just the texts.",
    links: [
      {
        url: "https://learnsanskrit.org/",
        title: "learnsanskrit.org",
        description:
          "A modern, friendly introduction to Sanskrit grammar. Aimed at the self-learner without a formal background.",
        glyph: "अ",
        hint: "Grammar tutor",
      },
      {
        url: "https://sanskrit.inria.fr/",
        title: "Sanskrit Heritage Site (INRIA)",
        description:
          "A grammatical analyser, declension/conjugation tools, and a heritage dictionary — invaluable when reading.",
        glyph: "व्या",
        hint: "Tools + dictionary",
      },
      {
        url: "https://www.wisdomlib.org/",
        title: "Wisdomlib",
        description:
          "An encyclopaedia-scale reference covering deities, scriptures, philosophical schools, and Āyurveda. Free, well-cross-linked.",
        glyph: "ज्ञ",
        hint: "Reference",
      },
    ],
  },
];

export default function LibraryResources() {
  return (
    <div className="flex flex-col gap-12">
      <header>
        <p className="page-eyebrow mb-1">Resources</p>
        <h2 className="font-display text-[1.7rem] sm:text-[2rem] leading-[1.15] tracking-tight">
          Study with the wider library, too
        </h2>
        <p className="font-grotesk text-[0.78rem] text-[var(--color-ink-soft)] mt-2 max-w-[64ch]">
          Sanskrit study is older than any one institution — the great
          repositories of texts, dictionaries, and recordings live across
          many sites. Here are the ones we lean on.
        </p>
      </header>

      {GROUPS.map((g) => (
        <section key={g.eyebrow}>
          <header className="mb-5">
            <p className="page-eyebrow mb-1">{g.eyebrow}</p>
            <h3 className="font-display text-[1.4rem] leading-[1.2] tracking-tight">{g.title}</h3>
            <p className="font-grotesk text-[0.78rem] text-[var(--color-ink-soft)] mt-2 max-w-[60ch]">
              {g.blurb}
            </p>
          </header>
          <ul className="featured-grid">
            {g.links.map((l) => (
              <li key={l.url} style={{ display: "flex" }}>
                <a
                  href={l.url}
                  target="_blank"
                  rel="noreferrer"
                  className="resource-link-card"
                >
                  <div className="resource-link-card-illustration">{l.glyph}</div>
                  <div className="resource-link-card-body">
                    {l.hint && (
                      <p className="font-grotesk text-[0.62rem] tracking-[0.18em] uppercase text-[var(--color-violet)]">
                        {l.hint}
                      </p>
                    )}
                    <p className="font-display text-[1.2rem] leading-[1.2] tracking-tight">
                      {l.title} <span className="opacity-60 ml-1">↗</span>
                    </p>
                    <p className="text-[0.9rem] leading-[1.5] text-[var(--color-ink-soft)]">
                      {l.description}
                    </p>
                    <p className="font-grotesk text-[0.66rem] text-[var(--color-ink-mute)] mt-auto truncate">
                      {tidy(l.url)}
                    </p>
                  </div>
                </a>
              </li>
            ))}
          </ul>
        </section>
      ))}

      <p className="font-display-italic text-[0.95rem] text-[var(--color-ink-mute)] max-w-[60ch]">
        Know a study site that belongs in this list? Tell us — we keep it
        small on purpose, but we're always open to one more good source.
      </p>
    </div>
  );
}

function tidy(url: string): string {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}
